import React, { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useParams } from "react-router-dom";
import SectionHeader from "../Components/SectionHeader";
import { lifestyleApi } from "../apiService/api";
import { useTranslation } from "react-i18next";

// Highcharts 12.x modules may export { default: fn } or fn directly depending
// on how webpack resolves them. This helper handles both shapes.
const applyHCModule = (mod) => {
  const fn = typeof mod === "function" ? mod : mod && mod.default;
  if (typeof fn === "function") fn(Highcharts);
};
applyHCModule(require("highcharts/modules/wordcloud"));

// ── Shimmer ──────────────────────────────────────────────────────────────────
const ensureShimmerKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("shimmer-keyframes")) return;
  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.innerHTML = `@keyframes shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position:  200% 0; }
  }`;
  document.head.appendChild(style);
};

const shimmerBase = {
  backgroundImage:
    "linear-gradient(90deg, var(--shimmer-1) 0%, var(--shimmer-2) 50%, var(--shimmer-1) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.3s linear infinite",
};

// ── Shared card container ─────────────────────────────────────────────────────
const cardStyle = {
  width: "100%",
  backgroundColor: "var(--bg-card)",
  borderRadius: "20px",
  border: "1px solid var(--border-subtle)",
  overflow: "hidden",
  marginBottom: 16,
};

// ── Skeleton ──────────────────────────────────────────────────────────────────
const CardSkeleton = ({ height = 200 }) => {
  useEffect(() => {
    ensureShimmerKeyframes();
  }, []);

  return (
    <div style={{ ...cardStyle, padding: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            ...shimmerBase,
            width: 32,
            height: 32,
            borderRadius: "50%",
          }}
        />
        <div
          style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}
        >
          <div
            style={{ ...shimmerBase, height: 16, width: "55%", borderRadius: 8 }}
          />
          <div
            style={{ ...shimmerBase, height: 12, width: "35%", borderRadius: 8 }}
          />
        </div>
      </div>
      <div style={{ ...shimmerBase, height, width: "100%", borderRadius: 12 }} />
    </div>
  );
};

// ── Stats grid row ────────────────────────────────────────────────────────────
const StatsRow = ({ label, value, isHeader }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: isHeader ? "1fr" : "1fr 1fr",
      padding: "7px 16px",
      borderBottom: "1px solid var(--border-subtle)",
      backgroundColor: isHeader ? "var(--bg-surface)" : "transparent",
      fontSize: 13,
    }}
  >
    {isHeader ? (
      <span style={{ fontWeight: 700, color: "var(--text-secondary)" }}>
        {label}
      </span>
    ) : (
      <>
        <span style={{ color: "var(--text-muted)" }}>{label}</span>
        <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>
          {value}
        </span>
      </>
    )}
  </div>
);

// ── Meal icons (from Angular component templates) ────────────────────────────
const ICON_STYLE = { filter: "grayscale(1)" };

const WORDCLOUD_ICONS = {
  breakfast: "https://storecdn.goqii.com/emailer/50997_1628073959.png",
  lunch:     "https://storecdn.goqii.com/emailer/12754_1628073981.png",
  dinner:    "https://storecdn.goqii.com/emailer/15214_1628074006.png",
  snacks:    "https://storecdn.goqii.com/emailer/48822_1628074021.png",
};
const WATER_ICON     = "https://goqii-app.s3.amazonaws.com/emailer/1619_1594801553.webp";
const CALENDAR_ICON  = "https://storecdn.goqii.com/emailer/84291_1629109935.png";

// ── Meal Word Cloud ───────────────────────────────────────────────────────────
const MealWordCloud = ({ userId, mealType }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    lifestyleApi
      .nutritionWordcloud(userId, mealType)
      .then((res) => {
        const d = res?.data?.data ?? (res?.data?.code === 200 ? res?.data?.data : null);
        if (d) setData(d);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userId, mealType]);

  if (loading) return <CardSkeleton height={200} />;
  if (!data || data.count === 0) return null;

  const options = {
    chart: {
      type: "wordcloud",
      backgroundColor: "transparent",
      height: 200,
      style: { fontFamily: "'Open Sans', sans-serif" },
    },
    title: { text: null },
    series: [
      {
        type: "wordcloud",
        data: data.datapoints || [],
        showInLegend: false,
        name: "Frequency",
      },
    ],
    accessibility: { enabled: false },
    credits: { enabled: false },
    tooltip: { enabled: false },
  };

  return (
    <div style={cardStyle}>
      <SectionHeader
        icon={WORDCLOUD_ICONS[mealType]}
        iconStyle={ICON_STYLE}
        title={data.title}
        subtitle={data.heading}
      />
      {data.datapoints?.length > 0 && (
        <div style={{ padding: "0 16px 16px" }}>
          <HighchartsReact highcharts={Highcharts} options={options} />
        </div>
      )}
    </div>
  );
};

// ── Meal Timeline ─────────────────────────────────────────────────────────────
const MealTimeline = ({ userId, mealType }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    lifestyleApi
      .nutritionTimeline(userId, mealType)
      .then((res) => {
        const d = res?.data?.data ?? (res?.data?.code === 200 ? res?.data?.data : null);
        if (d) setData(d);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userId, mealType]);

  if (loading) return <CardSkeleton height={165} />;
  if (!data || data.days === 0) return null;

  const options = {
    chart: {
      type: "spline",
      marginTop: 0,
      backgroundColor: "transparent",
      height: 165,
      style: { fontFamily: "'Open Sans', sans-serif" },
    },
    title: { text: null },
    xAxis: {
      categories: data.xAxis,
      labels: {
        style: { color: "var(--text-muted)", fontSize: "11px" },
      },
      lineColor: "var(--border-subtle)",
      tickColor: "transparent",
    },
    yAxis: {
      title: { text: null },
      gridLineWidth: 0,
      labels: { enabled: false },
    },
    series: [
      {
        name: "This Week",
        data: data.yAxis,
        color: "#FF8800",
        lineWidth: 2,
        dashStyle: "Dash",
        showInLegend: false,
        marker: { enabled: false },
      },
    ],
    legend: { enabled: false },
    tooltip: { enabled: false },
    credits: { enabled: false },
    plotOptions: { series: { animation: false } },
  };

  const rows = [{ label: data.stats, isHeader: true }];
  if (data.days) rows.push({ label: data.meal_log_days, value: data.days });
  if (data.skippedDays)
    rows.push({ label: data.meal_skipped_days, value: data.skippedDays });
  if (data.healthyMealCount)
    rows.push({ label: data.healthy_meal, value: data.healthyMealCount });
  if (data.homeFoodCount)
    rows.push({ label: data.home_meal, value: data.homeFoodCount });
  if (data.outsideFoodCount)
    rows.push({ label: data.outside_meal, value: data.outsideFoodCount });
  if (data.mealWithImages)
    rows.push({ label: data.meal_with_images, value: data.mealWithImages });
  if (data.time) rows.push({ label: data.meal_time, value: data.time });
  if (data.recommendation)
    rows.push({ label: data.recommendationStr, value: data.recommendation });

  return (
    <div style={cardStyle}>
      <div style={{ padding: "0 16px" }}>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
      <div style={{ borderTop: "1px solid var(--border-subtle)" }}>
        {rows.map((row, i) => (
          <StatsRow key={i} {...row} />
        ))}
      </div>
    </div>
  );
};

// ── Water Graph ───────────────────────────────────────────────────────────────
const WaterGraph = ({ userId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    lifestyleApi
      .nutritionWaterGraph(userId)
      .then((res) => {
        const d = res?.data?.data ?? (res?.data?.code === 200 ? res?.data?.data : null);
        if (d) setData(d);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <CardSkeleton height={165} />;
  if (!data || data.days === 0) return null;

  const options = {
    chart: {
      type: "column",
      marginTop: 5,
      backgroundColor: "transparent",
      height: 165,
      style: { fontFamily: "'Open Sans', sans-serif" },
    },
    title: { text: null },
    xAxis: {
      categories: data.xAxis,
      labels: {
        style: { color: "var(--text-muted)", fontSize: "11px" },
      },
      lineColor: "var(--border-subtle)",
      tickColor: "transparent",
    },
    yAxis: {
      title: { text: null },
      gridLineWidth: 0,
      labels: {
        formatter: function () {
          return this.value / 1000;
        },
        style: { color: "var(--text-muted)", fontSize: "10px" },
      },
    },
    series: [
      {
        name: "This Week",
        data: data.yAxis,
        color: "#7cb5ec",
        showInLegend: false,
        dataLabels: {
          enabled: true,
          style: {
            fontSize: "9px",
            fontWeight: 400,
            color: "var(--text-muted)",
            textOutline: "none",
          },
          formatter: function () {
            return this.y ? this.y / 1000 : "";
          },
        },
      },
    ],
    legend: { enabled: false },
    tooltip: { enabled: false },
    credits: { enabled: false },
    plotOptions: { series: { animation: false } },
  };

  const rows = [{ label: data.stats, isHeader: true }];
  if (data.days) rows.push({ label: data.water_log_days, value: data.days });
  if (data.avgWaterIntake)
    rows.push({ label: data.average_water_intake, value: data.avgWaterIntake });
  if (data.recommendation)
    rows.push({ label: data.recommendation, value: "" });

  return (
    <div style={cardStyle}>
      <SectionHeader
        icon={WATER_ICON}
        iconStyle={ICON_STYLE}
        title={data.title}
        subtitle={data.subtitle}
      />
      <div style={{ padding: "0 16px" }}>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
      <div style={{ borderTop: "1px solid var(--border-subtle)" }}>
        {rows.map((row, i) => (
          <StatsRow key={i} {...row} />
        ))}
      </div>
    </div>
  );
};

// ── Nutrition Calendar ────────────────────────────────────────────────────────
const NutritionCalendar = ({ userId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    lifestyleApi
      .nutritionMonthlyCalendar(userId)
      .then((res) => {
        const d = res?.data?.data ?? (res?.data?.code === 200 ? res?.data?.data : null);
        if (d) setData(d);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <CardSkeleton height={250} />;
  if (!data) return null;

  const dayHeaders = t("nutritionInsights.calendarDays", {
    returnObjects: true,
  });

  return (
    <div style={cardStyle}>
      <SectionHeader icon={CALENDAR_ICON} iconStyle={ICON_STYLE} title={data.title} subtitle={data.subtitle} />

      <div style={{ padding: "0 16px 20px" }}>
        {/* Calendar table */}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <thead>
            <tr>
              {dayHeaders.map((day, i) => (
                <th
                  key={i}
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-muted)",
                    backgroundColor: "var(--bg-surface)",
                    padding: "5px 0",
                    textAlign: "center",
                    textTransform: "uppercase",
                    border: "1px solid var(--border-subtle)",
                  }}
                >
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.datapoints || []).map((week, wi) => (
              <tr key={wi}>
                {week.map((cell, ci) => (
                  <td
                    key={ci}
                    style={{
                      height: 52,
                      border: "1px solid var(--border-subtle)",
                      backgroundColor: cell.bgcolor || "transparent",
                      verticalAlign: "top",
                      position: "relative",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        top: 4,
                        left: "10%",
                        fontSize: 12,
                        color: "#333",
                      }}
                    >
                      {cell.date}
                    </span>
                    {cell.logs ? (
                      <span
                        style={{
                          position: "absolute",
                          bottom: 3,
                          right: 4,
                          fontSize: 10,
                          color: "#555",
                        }}
                      >
                        {cell.logs}
                      </span>
                    ) : null}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Legend */}
        <div
          style={{
            display: "flex",
            gap: 16,
            marginTop: 10,
            fontSize: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 12,
                height: 12,
                backgroundColor: "#e3f4cb",
                display: "inline-block",
                borderRadius: 2,
                flexShrink: 0,
              }}
            />
            <span style={{ color: "var(--text-muted)" }}>
              {data.legend_food_logs}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 12,
                height: 12,
                backgroundColor: "#D3D3D3",
                display: "inline-block",
                borderRadius: 2,
                flexShrink: 0,
              }}
            />
            <span style={{ color: "var(--text-muted)" }}>
              {data.legend_no_food_logs}
            </span>
          </div>
        </div>

        {/* Recommendation */}
        {data.recommendation ? (
          <div
            style={{
              marginTop: 12,
              fontSize: 13,
              color: "var(--text-muted)",
              lineHeight: 1.5,
            }}
          >
            {data.recommendation}
          </div>
        ) : null}

        {/* Log Food button */}
        {data.foodUrl ? (
          <div
            style={{ marginTop: 14, display: "flex", justifyContent: "center" }}
          >
            <a href={data.foodUrl} style={{ textDecoration: "none" }}>
              <button
                style={{
                  backgroundColor: "#35b400",
                  color: "#fff",
                  border: "none",
                  borderRadius: 20,
                  padding: "10px 30px",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "'Open Sans', sans-serif",
                }}
              >
                {t("nutritionInsights.logFood")}
              </button>
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const NutritionInsights = () => {
  const { uid } = useParams();
  const mealTypes = ["breakfast", "lunch", "dinner", "snacks"];

  return (
    <div
      style={{
        backgroundColor: "var(--bg-page)",
        minHeight: "100vh",
        padding: 16,
        boxSizing: "border-box",
      }}
    >
      {mealTypes.map((mealType) => (
        <React.Fragment key={mealType}>
          <MealWordCloud userId={uid} mealType={mealType} />
          <MealTimeline userId={uid} mealType={mealType} />
        </React.Fragment>
      ))}
      <WaterGraph userId={uid} />
      <NutritionCalendar userId={uid} />
    </div>
  );
};

export default NutritionInsights;
